<template>
  <div v-if="isOpen" class="quote-modal-overlay" @click.self="closeModal">
    <div class="quote-modal-container">
      <!-- Drag Handle (移动端) -->
      <div class="drag-handle-wrapper" @click="closeModal">
        <div class="drag-handle"></div>
      </div>
      
      <!-- Header -->
      <div class="quote-modal-header">
        <div class="header-left">
          <div class="header-icon-wrapper">
            <svg class="header-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14.017 18L14.017 10.609C14.017 4.905 17.748 1.039 23 0L23.995 2.151C21.563 3.068 20 5.789 20 8H24V18H14.017ZM0 18L0 10.609C0 4.905 3.748 1.038 9 0L9.996 2.151C7.563 3.068 6 5.789 6 8H9.983L9.983 18L0 18Z"/>
            </svg>
          </div>
          <h3 class="header-title">{{ $t('kbDetailsModal.title') }}</h3>
        </div>
        <button class="close-btn" @click="closeModal">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
      </div>

      <!-- Content List -->
      <div class="quote-content-list hide-scrollbar">
        <div v-for="(item, index) in details" :key="index" class="quote-item">
          <div class="quote-item-header">
            <span class="quote-item-index">{{ index + 1 }}</span>
            <h4 class="quote-item-title">{{ getItemTitle(item, index) }}</h4>
            <span v-if="item.similarity" class="quote-item-score">{{ $t('kbDetailsModal.relevance', { score: (item.similarity * 100).toFixed(1) }) }}</span>
          </div>
          <div class="quote-item-content" :class="{ 'is-expanded': expandedItems[index] }">
            <MarkdownRenderer :content="item.content" />
            <div v-if="!expandedItems[index] && shouldShowExpand(item.content)" class="content-mask">
              <button class="expand-btn" @click="toggleExpand(index)">
                {{ $t('kbDetailsModal.expandAll') }}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-left: 4px;">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
            </div>
            <div v-else-if="expandedItems[index]" class="collapse-btn-wrapper">
              <button class="expand-btn" @click="toggleExpand(index)">
                {{ $t('kbDetailsModal.collapse') }}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-left: 4px;">
                  <polyline points="18 15 12 9 6 15"></polyline>
                </svg>
              </button>
            </div>
          </div>
          <div class="quote-item-footer">
            <span class="quote-item-domain">{{ getDomain(item.url || item.document_name) || $t('kbDetailsModal.localDocument') }}</span>
            <button v-if="item.url" class="quote-item-link-btn" @click="openLink(item.url)">{{ $t('kbDetailsModal.viewOriginal') }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MarkdownRenderer from './MarkdownRenderer.vue';

export default {
  name: 'KbDetailsModal',
  components: {
    MarkdownRenderer
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    details: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      expandedItems: {}
    };
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.expandedItems = {}; // 重置展开状态
      }
    }
  },
  methods: {
    getItemTitle(item, index) {
      return item.title || item.document_name || this.$t('kbDetailsModal.knowledgeSegment', { index: index + 1 });
    },
    toggleExpand(index) {
      this.$set(this.expandedItems, index, !this.expandedItems[index]);
    },
    shouldShowExpand(content) {
      if (!content) return false;
      // 简单判断内容长度，超过 150 个字符则认为需要展开，或者包含换行符超过 4 个
      return content.length > 150 || (content.match(/\n/g) || []).length >= 4;
    },
    closeModal() {
      this.$emit('close');
    },
    getDomain(url) {
      if (!url) return '';
      try {
        if (!url.startsWith('http')) return url;
        return new URL(url).hostname;
      } catch (e) {
        return url;
      }
    },
    openLink(url) {
      if (url && url.startsWith('http')) {
        window.open(url, '_blank');
      }
    }
  }
};
</script>

<style scoped>
.quote-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end; /* 移动端默认从底部弹出 */
  justify-content: center;
  animation: fadeIn 0.3s ease-out;
}

.quote-modal-container {
  width: 100%;
  height: 75vh;
  background-color: #F8F9FF;
  border-radius: 32px 32px 0 0;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.15);
  z-index: 1005;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.4s cubic-bezier(0.32, 0.72, 0, 1);
}

/* PC 端居中显示并调整圆角 */
@media screen and (min-width: 768px) {
  .quote-modal-overlay {
    align-items: center;
  }
  
  .quote-modal-container {
    width: 600px;
    height: auto;
    max-height: 80vh;
    border-radius: 24px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
  
  .drag-handle-wrapper {
    display: none !important;
  }
}

/* Drag Handle */
.drag-handle-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 12px;
  padding-bottom: 4px;
  cursor: pointer;
}

.drag-handle {
  width: 48px;
  height: 6px;
  background-color: #E5E7EB;
  border-radius: 9999px;
}

/* Header */
.quote-modal-header {
  display: flex;
  padding: 8px 20px 16px 20px;
  flex-shrink: 0;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-icon-wrapper {
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  background-color: rgba(26, 59, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1A3BFF;
}

.header-icon {
  width: 16px;
  height: 16px;
}

.header-title {
  font-size: 20px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.025em;
  margin: 0;
}

.close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
  background-color: #FFFFFF;
  border: none;
  border-radius: 9999px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.2s;
}

.close-btn:hover {
  color: #374151;
  background-color: #F3F4F6;
}

.close-btn:active {
  transform: scale(0.95);
}

/* Content List */
.quote-content-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Quote Item */
.quote-item {
  background-color: #FFFFFF;
  border-radius: 24px;
  padding: 16px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(243, 244, 246, 0.5);
}

.quote-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.quote-item-index {
  padding: 2px 8px;
  border-radius: 6px;
  background-color: rgba(26, 59, 255, 0.1);
  color: #1A3BFF;
  font-size: 12px;
  font-weight: 700;
}

.quote-item-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.quote-item-score {
  font-size: 12px;
  color: #059669;
  background: #D1FAE5;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
  flex-shrink: 0;
}

.quote-item-content {
  font-size: 14px;
  color: #4B5563;
  line-height: 1.625;
  font-weight: 500;
  position: relative;
  max-height: 120px; /* 限制默认高度，约 5 行文本 */
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.quote-item-content.is-expanded {
  max-height: none; /* 展开后自适应高度 */
}

.content-mask {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 80%);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 2px;
}

.collapse-btn-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}

.expand-btn {
  background: transparent;
  border: none;
  color: #1A3BFF;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
}

.expand-btn:hover {
  background: rgba(26, 59, 255, 0.05);
}

[data-theme="dark"] .content-mask {
  background: linear-gradient(to bottom, rgba(31,41,55,0) 0%, rgba(31,41,55,1) 80%);
}

[data-theme="dark"] .expand-btn {
  color: #60A5FA;
}

[data-theme="dark"] .expand-btn:hover {
  background: rgba(96, 165, 250, 0.1);
}

.quote-item-content :deep(p) {
  margin: 0 0 8px 0;
}

.quote-item-content :deep(p:last-child) {
  margin-bottom: 0;
}

.quote-item-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}

.quote-item-content :deep(a) {
  word-break: break-all;
}

.quote-item-footer {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.quote-item-domain {
  font-size: 12px;
  color: #9CA3AF;
  font-weight: 500;
}

.quote-item-link-btn {
  color: #1A3BFF;
  font-size: 12px;
  font-weight: 700;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

.quote-item-link-btn:hover {
  text-decoration: underline;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

/* Dark Mode Support */
[data-theme="dark"] .quote-modal-container {
  background-color: #111827;
}

[data-theme="dark"] .drag-handle {
  background-color: #374151;
}

[data-theme="dark"] .header-title {
  color: #F9FAFB;
}

[data-theme="dark"] .close-btn {
  background-color: #1F2937;
  color: #9CA3AF;
}

[data-theme="dark"] .close-btn:hover {
  background-color: #374151;
  color: #D1D5DB;
}

[data-theme="dark"] .quote-item {
  background-color: #1F2937;
  border-color: rgba(55, 65, 81, 0.5);
}

[data-theme="dark"] .quote-item-title {
  color: #F9FAFB;
}

[data-theme="dark"] .quote-item-content {
  color: #D1D5DB;
}

[data-theme="dark"] .header-icon-wrapper {
  background-color: rgba(96, 165, 250, 0.1);
  color: #60A5FA;
}

[data-theme="dark"] .quote-item-index {
  background-color: rgba(96, 165, 250, 0.1);
  color: #60A5FA;
}

[data-theme="dark"] .quote-item-link-btn {
  color: #60A5FA;
}
</style>
